import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { isHardwarePart } from './hardwareInteractionsPrompt';
import { coverIds, useHardwareInteractions, washerIds } from './hardwareInteractionsRun';
import { buildInsertionPrompt, type InsertionContext } from './insertionPrompt';
import { findInsertion, insertionShots, useInsertion } from './insertionRun';
import { useCatalogModels } from './partModels';
import { STEPS_MODEL } from './models';
import { useModelSizes, type ByPart } from './partModelSize';
import { namedIn, withLetter } from './partsCatalogue';
import { usePartsRun, type PartItem } from './partsRun';
import { TABLE, TABLE_CELL, TABLE_HEAD, TableCard } from './TableCard';
import { withWashers } from './HolesGrid';
import { useCategories } from './catalogCategories';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { useSince } from './waitClock';
import { useLanguage } from '../../contexts/LanguageContext';

// THE PARTS ASKED ABOUT AND TABLED: every piece of hardware, by stored id — bar the covers and the
// washers, as the Joints table names them (see coverIds, washerIds): a wood button or a felt pad goes
// into no hole of its own, and a washer goes in with the piece it is on, which is asked about with it.
const askedIds = (items: PartItem[], table: InsertionContext['table']) => {
  const left = new Set([...coverIds(table), ...washerIds(table)]);
  return items.flatMap((p, i) => (isHardwarePart(p) && !left.has(String(i)) ? [String(i)] : []));
};

// THE QUESTION, built here and used by both the run and the badge — so the preview is byte for
// byte what is sent.
const contextFor = (
  items: PartItem[], sizes: ByPart, table: InsertionContext['table'],
): InsertionContext => ({ items, sizes, table, ids: askedIds(items, table) });

// THE INSERTION TABLE'S CALL, as a badge beside Hardware & covers' other two: its Run, the
// question it asks and what came back — see insertionRun.
export function HardwareAxisBadge({ docId, locked }: { docId: string; locked?: boolean }) {
  const { t } = useLanguage();
  const { items } = usePartsRun(docId);
  const sizes = useModelSizes(docId).byPart;
  const table = useHardwareInteractions(docId);
  const found = useInsertion(docId);
  // EACH PART'S MODEL IN THE PARTS DB — the one its sizes were measured off (see ModelSizeSync).
  const { models } = useCatalogModels();
  const jobOf = (id: string) => models.find((m) => m.id === sizes[id]?.job) ?? null;
  // Subscribed so the prompt rebuilds once the ontology lands: until then no part reads as hardware.
  const cats = useCategories();
  const ctx = useMemo(
    () => contextFor(items, sizes, table),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, sizes, table, cats],
  );
  // Not while the Joints table is asked again: the question is built from its answer.
  const canRun = ctx.ids.length > 0 && !found.busy && !table.busy && !locked;
  const running = useSince(found.startedAt);
  const took = found.startedAt != null ? running : found.elapsed;
  return (
    <CallBadge
      title={t('notice.hardwareAxis.title')}
      named
      run={{
        label: t('notice.hardwareAxis.run'),
        onRun: () => { if (canRun) void findInsertion(docId, ctx, jobOf, STEPS_MODEL, t); },
        busy: found.busy,
        disabled: !canRun,
        elapsed: took,
      }}
      promptLabel={t('notice.hardwareAxis.title')}
      // THE PICTURES TOO, rendered as the run renders them, so the preview is what is sent.
      load={async () => {
        if (!ctx.ids.length) return { prompt: t('notice.hardwareAxis.noHardware'), model: STEPS_MODEL };
        const shot = await insertionShots(ctx, jobOf);
        return { prompt: buildInsertionPrompt(shot.ctx), urls: shot.images, model: STEPS_MODEL };
      }}
      answer={found.raw ? {
        label: t('notice.cat.reasoningTitle'),
        content: reasoningTooltip(found.reasoning, found.raw, t, null),
      } : undefined}
    />
  );
}

// HOW EACH PIECE OF HARDWARE GOES INTO ITS HOLES, one row per hardware part (no cover, no washer): its 3D
// model's dimensions, and as the insertion table's call answered (see insertionRun) along which
// axis of that model it goes in, how wide its hole is, and how much of it is inside the parts and
// outside them. Facts about the hardware and not about any one hole — one screw is shared between
// the holes it runs through — so said here, once per part. A part the call has not answered is
// said so in the warning colour: the holes it goes into have no width or depth.
const cap = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export function HardwareAxisTable({ docId }: { docId: string }) {
  const { t, language } = useLanguage();
  const { items } = usePartsRun(docId);
  const sizes = useModelSizes(docId).byPart;
  const found = useInsertion(docId);
  const table = useHardwareInteractions(docId);
  // Subscribed so the rows are found once the ontology lands.
  useCategories();
  const rows = askedIds(items, table).map((id) => ({ p: items[Number(id)], id }));
  // EVERY WASHER AND THE PIECE IT IS ON, over the whole table.
  const washersOn = Object.assign({}, ...table.interactions.map((one) => one.washers)) as Record<string, string>;
  if (!rows.length) return null;
  // WHILE THE CALL IS OUT, A SPINNER IN THE TABLE'S PLACE: the answer on screen is the one about to
  // be replaced. And while the Joints table is asked again: which parts are asked, and the holes
  // they go into, are its answer.
  if (found.busy || table.busy) {
    return (
      <TableCard title={t('notice.hardwareAxis.title')}>
        <Loader2 className="mx-auto my-6 h-6 w-6 animate-spin text-slate-400" />
      </TableCard>
    );
  }
  return (
    <TableCard title={t('notice.hardwareAxis.title')}>
      {found.error && <p className="mb-2 text-center text-sm text-rose-600">{found.error}</p>}
      <table className={`${TABLE.replace('w-full', 'w-auto')} mx-auto tabular-nums`}>
        <thead>
          <tr>
            <th className={TABLE_HEAD}>{t('notice.hardwareAxis.part')}</th>
            <th className={TABLE_HEAD}>{t('notice.hardwareAxis.dims')}</th>
            <th className={TABLE_HEAD}>{t('notice.hardwareAxis.axis')}</th>
            <th className={TABLE_HEAD}>{t('notice.hardwareAxis.width')}</th>
            <th className={TABLE_HEAD}>{t('notice.hardwareAxis.length')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, id }) => {
            const size = sizes[id];
            const answer = found.byPart[id];
            return (
              <tr key={id}>
                {/* THE PART, and the washers the Joints table adds to it — they are asked about with it. */}
                <td className={TABLE_CELL}>
                  {withWashers(withLetter(namedIn(p, language) || `#${id}`, p.letter), Object.entries(washersOn)
                    .filter(([, on]) => on === id)
                    .map(([w]) => withLetter(namedIn(items[Number(w)], language) || `#${w}`, items[Number(w)]?.letter)))}
                </td>
                {/* WHAT ITS 3D MODEL MEASURES, along the app's three axes (see partModelSize). */}
                <td className={TABLE_CELL}>
                  {size && (
                    <div className="whitespace-nowrap">
                      {(['x', 'y', 'z'] as const).map((a) => (
                        <div key={a}>{`${cap(t(`notice.hardwareAxis.axis.${a}`))}: ${Math.round(size[a] * 10) / 10}`}</div>
                      ))}
                    </div>
                  )}
                </td>
                {!answer ? (
                  <td colSpan={3} className={`${TABLE_CELL} text-amber-600`}>{t('notice.hardwareAxis.unanswered')}</td>
                ) : (
                  <>
                    <td className={TABLE_CELL}>{`${answer.axis.toUpperCase()} (${t(`notice.hardwareAxis.axis.${answer.axis}`)})`}</td>
                    {/* NO DIAMETER IS NO HOLE: the call answers null for a part that goes into none. */}
                    <td className={TABLE_CELL}>
                      {answer.holeDiameterMm ?? t('notice.hardwareAxis.noHole')}
                    </td>
                    {/* A LENGTH THE CALL GAVE NONE OF IS NO LINE. */}
                    {/* INSIDE THE PART, THEN OUTSIDE IT, one line each. */}
                    <td className={`${TABLE_CELL} whitespace-nowrap`}>
                      {answer.insideMm != null && <div>{`${answer.insideMm} ${t('notice.hardwareAxis.inside')}`}</div>}
                      {answer.outsideMm != null && <div>{`${answer.outsideMm} ${t('notice.hardwareAxis.outside')}`}</div>}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableCard>
  );
}
